import { css } from '@emotion/react';
import { faSync } from '@fortawesome/pro-solid-svg-icons';
import {
  type FC, useCallback, useEffect, useState,
} from 'react';
import { TextInputComponent } from '~/_shared/baseComponents/inputs';
import {
  createColor, createColorWithOpacity, guaranteeHash,
} from '~/_shared/components/colorPicker/colorPicker.helpers';
import { type ColorResult } from '~/_shared/components/colorPicker/colorPicker.types';
import { useTheme } from '~/_shared/themes/theme.hooks';
import type { Theme } from '~/_shared/themes/theme.model';
import { type ThemeProps } from '~/_shared/types/themeProps';
import { validateValueHasOnlyNumericCharacters } from '~/_shared/utils/form/form.helpers';
import { useTranslation } from '~/_shared/utils/hooks';
import { formatValue } from '~/_shared/utils/range/range.helpers';
import { BoundaryTerritoryListingLimit } from '~/boundary/constants';
import { useLoadMoreListing } from '~/boundary/hooks/useLoadMoreListing';
import { BoundarySettingsLoadMoreRowComponent } from '~/boundary/settings/table/row/BoundarySettingsLoadMoreRow.component';
import { isBoundaryTerritoryEmpty } from '~/sidebar/sidebarApps/mapTools/boundary/fill/boundaryFill.helpers';
import type { BoundaryTerritoryId } from '~/store/boundaries/boundaryIdentifier.type';
import { useBoundaryTerritoryAssignments } from '~/store/boundaryTerritoryDetails/boundaryTerritoryDetails.selectors';
import {
  type BoundaryTerritory, type BoundaryTerritoryGroup,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import {
  BOUNDARY_MAX_VALUE_ID, BOUNDARY_MIN_VALUE_ID, isBoundaryTerritoryHidden, isBoundaryTerritorySpecial,
  translateBoundaryTerritoryDisplayName,
} from '../../../../../boundary/settings/boundarySettings.helpers';
import { BoundarySettingsCustomTerritoryDataComponent } from '../../../../../boundary/settings/custom/boundarySettingsCustomTerritoryDataComponent';
import {
  getBoundaryTerritoryRangeValues, handleBoundaryTerritoryValueToChange, validateBoundarySettingsNumericValue,
} from '../../../../../boundary/settings/numeric/boundarySettingsNumeric.helpers';
import { BoundarySettingsRestoreButtonComponent } from '../../../../../boundary/settings/restoreButton/boundarySettingsRestoreButton.component';
import { BoundarySettingsTableComponent } from '../../../../../boundary/settings/table/boundarySettingsTable.component';
import { BoundarySettingsTableRowComponent } from '../../../../../boundary/settings/table/row/boundarySettingsTableRow.component';

const dataLabelStyle = css({
  textTransform: 'uppercase',
});

const tablePrimaryDataStyle = css({
  display: 'flex',
  alignItems: 'center',
  whiteSpace: 'nowrap',
});

const tableToLabelStyle = css({
  padding: '0 5px',
});

const tableInputStyle = ({ theme, isValid }: ThemeProps<{ isValid: boolean }>) => css({
  background: theme.backgroundColors.secondary,
  minWidth: 50,
  borderColor: isValid ? undefined : theme.borderColors.error,
});

const toValueStyle = css({
  maxWidth: 120,
  overflow: 'hidden',
  display: 'block',
  textOverflow: 'ellipsis',
});

const fromValueStyle = css({
  maxWidth: 120,
  overflow: 'hidden',
  display: 'block',
  textOverflow: 'ellipsis',
});

const valueToWrapperStyle = css({
  display: 'flex',
  alignItems: 'center',
  flex: 1,
});

const addTopsideDelimiterStyle = (theme: Theme) => css({
  borderTop: `1px solid ${theme.borderColors.primary}`,
});

const footerStyle = css({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: 10,
});

type BoundaryFillSettingsNumericTerritoriesTableProps = Readonly<{
  boundaryTerritoryGroup: BoundaryTerritoryGroup;
  rangeMin: number | typeof BOUNDARY_MIN_VALUE_ID;
  rangeMax: number | typeof BOUNDARY_MAX_VALUE_ID;
  dataTableValueSuffix: string;
  isOpacityShared?: boolean;
  isDecimalMode: boolean;
  onBoundaryTerritoryGroupChange: (territoryGroup: BoundaryTerritoryGroup) => void;
  onBoundaryTerritoryChange: (territories: ReadonlyArray<BoundaryTerritory>) => void;
  onTerritoryNameChange?: (boundaryTerritoryId: BoundaryTerritoryId, newName: string) => void;
  onRecalculateRangesClick: () => void;
}>;

export const BoundaryFillSettingsNumericTerritoriesTableComponent: FC<BoundaryFillSettingsNumericTerritoriesTableProps> = ({
  boundaryTerritoryGroup,
  rangeMin,
  rangeMax,
  dataTableValueSuffix,
  isOpacityShared,
  isDecimalMode,
  onBoundaryTerritoryGroupChange,
  onBoundaryTerritoryChange,
  onTerritoryNameChange,
  onRecalculateRangesClick,
}) => {
  const [t] = useTranslation();
  const theme = useTheme();

  const style = boundaryTerritoryGroup.settings.style;
  const boundaryTerritories = boundaryTerritoryGroup.settings.boundaryTerritories;
  const lineWidth = style.lineWidth;

  const boundaryTerritoryAssignments = useBoundaryTerritoryAssignments();

  const {
    limit: boundarySettingListCountLimit,
    increaseLimit: increaseBoundarySettingListCountLimit,
  } = useLoadMoreListing(BoundaryTerritoryListingLimit);

  // holds the in-progress (uncommitted) value-to text for the row currently being edited
  const [editingValueTo, setEditingValueTo] = useState<{ id: BoundaryTerritoryId; value: string } | null>(null);

  useEffect(() => {
    setEditingValueTo(null);
  }, [boundaryTerritoryGroup.boundaryTerritoryGroupId]);

  const handleValueToChange = useCallback((id: BoundaryTerritoryId, value: string) => {
    if (!validateValueHasOnlyNumericCharacters(value, { isDecimal: isDecimalMode })) {
      return;
    }

    setEditingValueTo({ id, value });
  }, [isDecimalMode]);

  const handleValueToBlur = useCallback((index: number) => {
    if (!editingValueTo) {
      return;
    }

    onBoundaryTerritoryChange(
      handleBoundaryTerritoryValueToChange(index, editingValueTo.value, boundaryTerritories, rangeMin, rangeMax, isDecimalMode),
    );
    setEditingValueTo(null);
  }, [boundaryTerritories, editingValueTo, isDecimalMode, onBoundaryTerritoryChange, rangeMin, rangeMax]);

  const handleChangeBoundaryTerritoryColor = useCallback((id: BoundaryTerritoryId) => (color: ColorResult) => {
    onBoundaryTerritoryGroupChange({
      ...boundaryTerritoryGroup,
      settings: {
        ...boundaryTerritoryGroup.settings,
        boundaryTerritories: boundaryTerritories.map((bt) => {
          if (id !== bt.boundaryTerritoryId) {
            return bt;
          }
          else {
            return {
              ...bt,
              style: {
                ...bt.style,
                color: guaranteeHash(color.hex),
                opacity: color.rgba.a * 100,
              },
            } satisfies BoundaryTerritory;
          }
        }),
      },
    });
  }, [boundaryTerritories, boundaryTerritoryGroup, onBoundaryTerritoryGroupChange]);

  if ((!boundaryTerritories.length)) {
    return null;
  }

  return (
    <>
      <BoundarySettingsTableComponent
        css={addTopsideDelimiterStyle}
        dataHeadingLabel={t('Number Ranges')}
        hideOpacity={isOpacityShared}
        dataRows={(
          <>
            {boundaryTerritories.map((item, index) => {
              const isEmpty = isBoundaryTerritoryEmpty(boundaryTerritoryAssignments,
                boundaryTerritoryGroup.boundaryTerritoryGroupId,
                item);
              const isHidden = isBoundaryTerritoryHidden(item,
                isEmpty,
                boundaryTerritoryGroup.settings.boundaryTerritoryType);
              if (isHidden || (index >= boundarySettingListCountLimit)) {
                return null;
              }

              const values = getBoundaryTerritoryRangeValues(item);
              const fromValue = values && values.valueFrom !== BOUNDARY_MIN_VALUE_ID ? values.valueFrom : rangeMin;
              const toValue = values && values.valueTo !== BOUNDARY_MAX_VALUE_ID ? values.valueTo : rangeMax;

              const fromValueFormatted = fromValue !== BOUNDARY_MIN_VALUE_ID ?
                formatValue(fromValue.toString()) + dataTableValueSuffix :
                t('min');
              const toValueFormatted = toValue !== BOUNDARY_MAX_VALUE_ID ?
                formatValue(toValue.toString()) + dataTableValueSuffix :
                t('max');
              const isSpecial = isBoundaryTerritorySpecial(item);

              const isRowValid = validateBoundarySettingsNumericValue(
                item,
                boundaryTerritories[index - 1] ?? null,
                boundaryTerritories[index + 1] ?? null,
              );

              const color = createColorWithOpacity(
                item.style.color,
                item.style.opacity / 100,
              );

              return (
                <BoundarySettingsTableRowComponent
                  key={item.boundaryTerritoryId}
                  index={index + 1}
                  data={item.custom && onTerritoryNameChange ? (
                    <BoundarySettingsCustomTerritoryDataComponent
                      territory={item}
                      onTerritoryNameChange={onTerritoryNameChange}
                    />
                  ) : isSpecial ? (
                    <span css={dataLabelStyle}>{translateBoundaryTerritoryDisplayName(item, t)}</span>
                  ) : (
                    <div css={tablePrimaryDataStyle}>
                      <span
                        title={fromValueFormatted}
                        css={fromValueStyle}
                      >
                        {t('{{value}}+', { value: fromValueFormatted })}
                      </span>
                      <div css={valueToWrapperStyle}>
                        <span css={tableToLabelStyle}>{t('toNumber')}</span>
                        {values && values.valueTo !== BOUNDARY_MAX_VALUE_ID ? (
                          <TextInputComponent
                            css={tableInputStyle({ theme, isValid: isRowValid })}
                            onChange={value => handleValueToChange(item.boundaryTerritoryId, value)}
                            onBlur={() => handleValueToBlur(index)}
                            value={editingValueTo?.id === item.boundaryTerritoryId ? editingValueTo.value : values.valueTo}
                            title={toValueFormatted}
                            icon={null}
                          />
                        ) : (
                          <span
                            title={toValueFormatted}
                            css={toValueStyle}
                          >
                            {toValueFormatted}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  lineColor={createColor(style.lineColor)}
                  lineWidth={lineWidth}
                  color={color}
                  hideOpacity={isOpacityShared}
                  onColorChange={handleChangeBoundaryTerritoryColor(item.boundaryTerritoryId)}
                />
              );
            })}
            {(boundaryTerritories.length > boundarySettingListCountLimit) && (
              <BoundarySettingsLoadMoreRowComponent onClick={increaseBoundarySettingListCountLimit} />
            )}
          </>
        )}
      />
      <div css={[footerStyle, addTopsideDelimiterStyle]}>
        <BoundarySettingsRestoreButtonComponent
          prefixIcon={faSync}
          text={t('Recalculate Equal Ranges')}
          onClick={onRecalculateRangesClick}
        />
      </div>
    </>
  );
};
